import WalletConnectorController, { COPY, isMobileAdapter, withinWalletAnswerLimit } from "../lib/wallet_connector_controller"
import { setBusy } from "../lib/work_status"
import { bytesToBase64 } from "../lib/solana_tx"

// The stages of a wallet sign-in, as the step line names them: the wallet
// connects, it signs the one free message, the server opens the session.
export const STEPS = [ "Connect", "Confirm", "Sign in" ]

// Signing in (R1): the wallet proves it holds the address by signing a nonce
// the server just issued. Nothing is sent, so no transaction and no fee.
export default class extends WalletConnectorController {
  static values = {
    nonceUrl: String,
    sessionUrl: String
  }

  selectWallet(connectorId) {
    return this.signIn(connectorId)
  }

  async signIn(connectorId) {
    this.clearError()
    setBusy(this.listTarget.querySelector(`[data-connector-id="${CSS.escape(connectorId)}"]`), true)
    this.status("Check your wallet to confirm", "waiting", { steps: STEPS, step: 0 })

    let address, account
    try {
      const selected = await this.openWallet(connectorId)
      address = selected.address
      account = selected.account
      // Coming back from the wallet app re-renders the list; a connected
      // page keeps what it shows (the confirm step) instead.
      this.connected = true
    } catch (error) {
      this.log("connect failed", error)
      return this.fail(error.rejected ? COPY.rejected : error.userMessage || COPY.connectFailed)
    }

    let message
    try {
      message = await this.requestMessage(address)
    } catch (error) {
      this.log("nonce failed", error)
      return this.fail(error.userMessage || COPY.network)
    }

    // On a phone the signature is a second trip to the wallet app, and Chrome
    // opens an app only in answer to a tap: the message is ready, and the
    // next tap carries it there.
    if (isMobileAdapter(this.client.getConnector(connectorId))) {
      return this.offerConfirmTap(() => this.finishSignIn(connectorId, account, address, message))
    }
    return this.finishSignIn(connectorId, account, address, message)
  }

  offerConfirmTap(proceed) {
    this.status(COPY.mobileConnected, "waiting", { steps: STEPS, step: 1 })
    this.listTarget.querySelectorAll("button, [data-phone-hint]").forEach((node) => node.remove())
    const button = document.createElement("button")
    button.type = "button"
    button.className =
      "w-full btn-marigold min-h-14 px-6 text-base cursor-pointer"
    button.textContent = COPY.mobileConfirmButton
    // Busy while the wallet app has the request; itself again if it fails,
    // so the same tap can be tried once more.
    button.addEventListener("click", async () => {
      setBusy(button, true)
      await proceed()
      if (!this.errorTarget.hidden) setBusy(button, false)
    })
    this.listTarget.append(button)
    this.listTarget.hidden = false
  }

  async finishSignIn(connectorId, account, address, message) {
    this.status("Check your wallet to confirm", "waiting", { steps: STEPS, step: 1 })
    let signature
    try {
      signature = await this.signMessage(connectorId, account, message)
    } catch (error) {
      this.log("confirm failed", error)
      this.connected = false
      return this.fail(COPY.confirmFailed)
    }

    this.status("Signing you in…", "working", { steps: STEPS, step: 2 })
    try {
      const { redirect_to } = await this.postJSON(this.sessionUrlValue, {
        address,
        message,
        signature
      })
      window.location.assign(redirect_to)
    } catch (error) {
      this.log("sign-in rejected", error)
      this.fail(error.userMessage || COPY.network)
    }
  }

  async requestMessage(address) {
    const { message } = await this.postJSON(this.nonceUrlValue, { address })
    return message
  }

  async signMessage(connectorId, account, message) {
    const connector = this.client.getConnector(connectorId)
    const feature = connector?.features?.["solana:signMessage"]
    if (!feature) throw new Error("wallet cannot sign messages")

    const bytes = new TextEncoder().encode(message)
    const [result] = await withinWalletAnswerLimit(feature.signMessage({ account, message: bytes }))
    return bytesToBase64(result.signature)
  }
}
